import React, { useMemo } from 'react';
import { TrendingUp, Info } from 'lucide-react';
import { CalculatedMetrics } from '../../types';

interface CorrelationAnalysisProps {
  data: CalculatedMetrics[];
}

interface CorrelationPair {
  metric1: string;
  metric2: string;
  correlation: number;
  strength: string;
  color: string;
}

export const CorrelationAnalysis: React.FC<CorrelationAnalysisProps> = ({ data }) => {
  const correlations = useMemo((): CorrelationPair[] => {
    if (data.length === 0) return [];

    const calculateCorrelation = (arr1: number[], arr2: number[]): number => {
      const n = arr1.length;
      if (n === 0) return 0;

      const mean1 = arr1.reduce((sum, val) => sum + val, 0) / n;
      const mean2 = arr2.reduce((sum, val) => sum + val, 0) / n;

      let numerator = 0;
      let sum1 = 0;
      let sum2 = 0;

      for (let i = 0; i < n; i++) {
        const diff1 = arr1[i] - mean1;
        const diff2 = arr2[i] - mean2;
        numerator += diff1 * diff2;
        sum1 += diff1 * diff1;
        sum2 += diff2 * diff2;
      }

      const denominator = Math.sqrt(sum1 * sum2);
      return denominator === 0 ? 0 : numerator / denominator;
    };

    const getStrength = (corr: number): { strength: string; color: string } => {
      const abs = Math.abs(corr);
      if (abs >= 0.7) return { strength: 'Molto Forte', color: '#10B981' };
      if (abs >= 0.5) return { strength: 'Forte', color: '#3B82F6' };
      if (abs >= 0.3) return { strength: 'Moderata', color: '#F59E0B' };
      return { strength: 'Debole', color: '#6B7280' };
    };

    const spikePercents = data.map(d => (d['Spike %'] || 0) * 100);
    const volumeRatios = data.map(d => d['Vol/PM Vol Ratio'] || 0);
    const marketCaps = data.map(d => Math.log(Number(d['Market Cap']) || 1));
    const retracements = data.map(d => {
      const ret = ((d.Close - d.High) / d.High) * 100;
      return isFinite(ret) ? Math.abs(ret) : 0;
    });
    const redCloses = data.map(d => d['Red Close'] || 0);

    const pairs = [
      {
        metric1: 'Spike %',
        metric2: 'Volume Ratio',
        values1: spikePercents,
        values2: volumeRatios
      },
      {
        metric1: 'Spike %',
        metric2: 'Ritracciamento %',
        values1: spikePercents,
        values2: retracements
      },
      {
        metric1: 'Spike %',
        metric2: 'Chiusura Rossa',
        values1: spikePercents,
        values2: redCloses
      },
      {
        metric1: 'Volume Ratio',
        metric2: 'Ritracciamento %',
        values1: volumeRatios,
        values2: retracements
      },
      {
        metric1: 'Market Cap (log)',
        metric2: 'Spike %',
        values1: marketCaps,
        values2: spikePercents
      },
      {
        metric1: 'Market Cap (log)',
        metric2: 'Ritracciamento %',
        values1: marketCaps,
        values2: retracements
      }
    ];

    return pairs.map(pair => {
      const corr = calculateCorrelation(pair.values1, pair.values2);
      const { strength, color } = getStrength(corr);
      return {
        metric1: pair.metric1,
        metric2: pair.metric2,
        correlation: corr,
        strength,
        color
      };
    }).sort((a, b) => Math.abs(b.correlation) - Math.abs(a.correlation));
  }, [data]);

  return (
    <div className="bg-gray-900/30 border border-gray-600 rounded-lg p-4 sm:p-6">
      <div className="flex items-center space-x-2 sm:space-x-3 mb-6">
        <div className="p-1.5 sm:p-2 bg-purple-400/10 rounded-lg">
          <TrendingUp className="h-4 w-4 sm:h-5 sm:w-5 text-purple-400" />
        </div>
        <div>
          <h4 className="text-base sm:text-lg font-semibold text-white">Analisi delle Correlazioni</h4>
          <p className="text-xs sm:text-sm text-gray-400">
            Correlazioni tra metriche chiave di spike e performance
          </p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-6">
        {correlations.map((corr, index) => (
          <div
            key={index}
            className="bg-gray-800/50 border border-gray-600 rounded-lg p-4"
          >
            <div className="flex items-center justify-between mb-3">
              <div className="flex-1">
                <p className="text-sm font-medium text-white">
                  {corr.metric1} ↔ {corr.metric2}
                </p>
                <p className="text-xs text-gray-400 mt-1">
                  Forza: <span style={{ color: corr.color }}>{corr.strength}</span>
                </p>
              </div>
              <div className="text-right">
                <p className="text-2xl font-bold" style={{ color: corr.color }}>
                  {corr.correlation.toFixed(3)}
                </p>
              </div>
            </div>

            <div className="relative w-full h-2 bg-gray-700 rounded-full overflow-hidden">
              <div
                className="absolute top-0 h-full rounded-full transition-all"
                style={{
                  backgroundColor: corr.color,
                  width: `${Math.abs(corr.correlation) * 100}%`,
                  left: corr.correlation >= 0 ? '50%' : `${50 - Math.abs(corr.correlation) * 50}%`
                }}
              />
              <div className="absolute top-0 left-1/2 w-0.5 h-full bg-white/30" />
            </div>

            <div className="flex justify-between text-xs text-gray-500 mt-1">
              <span>Negativa</span>
              <span>Positiva</span>
            </div>
          </div>
        ))}
      </div>

      <div className="bg-blue-400/10 border border-blue-400/30 rounded-lg p-4">
        <div className="flex items-start space-x-2">
          <Info className="h-4 w-4 text-blue-400 flex-shrink-0 mt-0.5" />
          <div>
            <h5 className="text-sm font-medium text-blue-400">Interpretazione</h5>
            <ul className="text-xs text-blue-300 mt-2 space-y-1">
              <li>• Valori vicini a +1: correlazione positiva forte (variabili si muovono insieme)</li>
              <li>• Valori vicini a -1: correlazione negativa forte (variabili si muovono in direzioni opposte)</li>
              <li>• Valori vicini a 0: nessuna correlazione lineare significativa</li>
              <li>• Correlazioni forti possono indicare relazioni predittive tra metriche</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};
